/**
 * Employee directory — company phone book for all authenticated roles.
 * Shows Active employees with all emails and phones (searchable).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { BookUser, Mail, Phone, Search } from 'lucide-react';
import { useEmployeeDirectory } from '../api.js';
import { Input } from '../../../shell/Field.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { PersonAvatar, employeeAvatarSrc } from '../../../shell/PersonAvatar.js';

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function EmployeeDirectoryPage() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 250);
  const { data, isLoading } = useEmployeeDirectory({
    search: debouncedSearch || undefined,
  });

  const items = data?.items ?? [];
  const departments = useMemo(() => {
    const set = new Set(items.map((e) => e.department).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const [deptFilter, setDeptFilter] = useState<string>('all');
  const filtered = useMemo(() => {
    if (deptFilter === 'all') return items;
    return items.filter((e) => e.department === deptFilter);
  }, [items, deptFilter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Employee directory</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Company phone book — emails and numbers for everyone on the team.
          </p>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {filtered.length} people
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[16rem] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search name, email, phone, department…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          aria-label="Filter by department"
        >
          <option value="all">All departments</option>
          {departments.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <p className="py-12 text-center text-sm text-slate-400">Loading directory…</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<BookUser className="h-8 w-8" />}
          title="No people found"
          description="Try a different search or department filter."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((person) => {
            const emails = person.contacts?.filter((c) => c.kind === 'email') ?? [];
            const phones = person.contacts?.filter((c) => c.kind === 'phone') ?? [];
            return (
              <article
                key={person.id}
                className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="flex items-start gap-3">
                  <PersonAvatar
                    name={person.name}
                    src={employeeAvatarSrc(person.id, person.avatarStorageKey)}
                    size="md"
                    tone="inverse"
                  />
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/employees/${person.id}`}
                      className="font-semibold text-slate-900 hover:underline dark:text-slate-100"
                    >
                      {person.name}
                    </Link>
                    <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                      {person.title} · {person.department}
                    </p>
                  </div>
                </div>

                <div className="mt-3 space-y-2 border-t border-slate-100 pt-3 dark:border-slate-800">
                  {emails.length === 0 && phones.length === 0 ? (
                    <p className="text-xs text-slate-400">No contact details on file.</p>
                  ) : (
                    <>
                      {emails.map((c) => (
                        <a
                          key={c.id}
                          href={`mailto:${c.value}`}
                          className="flex items-center gap-2 text-sm text-slate-700 hover:text-sky-600 dark:text-slate-300 dark:hover:text-sky-400"
                        >
                          <Mail className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{c.value}</span>
                          <span className="ml-auto shrink-0 text-[10px] uppercase text-slate-400">
                            {c.label}{c.isPrimary ? ' ★' : ''}
                          </span>
                        </a>
                      ))}
                      {phones.map((c) => (
                        <a
                          key={c.id}
                          href={`tel:${c.value}`}
                          className="flex items-center gap-2 text-sm text-slate-700 hover:text-sky-600 dark:text-slate-300 dark:hover:text-sky-400"
                        >
                          <Phone className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{c.value}</span>
                          <span className="ml-auto shrink-0 text-[10px] uppercase text-slate-400">
                            {c.label}{c.isPrimary ? ' ★' : ''}
                          </span>
                        </a>
                      ))}
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
